import React, { useEffect, useRef, useState } from 'react';
import ReactDom from 'react-dom';
import { useSelector, Provider, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { loadModules } from 'esri-loader';
import { basemaps } from 'app/contants';
import Settings from './settings';
import { useComponentMount } from 'utilities/hooks.util';
import { Loading, Button } from 'carbon-components-react';
import { isBrowser } from 'react-device-detect';
import { selectPivateTeamBaseMap } from 'redux/app/selectors';
import {
  setBaseMap,
  addAlert,
  loadCustomBaseMapStart,
  loadCustomBaseMapSuccess
} from 'redux/session/actions';
import {
  selectBaseMap,
  currentUser,
  selectScreenSize,
  selectCustomBaseMap
} from 'redux/session/selectors';
import {
  selectCurrentTeamBaseMapId,
  selectCurrentTeamBaseMapType,
  selectCurrentTeamBaseMapToken,
  selectCurrentTeamShape
} from 'redux/team/selectors';
import { MAP_DEFAULT_VAR, BaseMap, SCREEN_SIZE } from 'types';
import { Add16, Subtract16, Location16, Home16 } from '@carbon/icons-react';
import { zoomToCenter, zoomToFeature, hoverPin, removePin } from './helper';
import { MapStyleControllerButton } from 'components/MapStyleController';
import { MapLocationSearchButton } from 'components/MapLocationSearch';
import { geojsonToArcGIS } from '@terraformer/arcgis';
import { configureStore as store } from 'redux/store';

const onClickCurrentLocation = () => {
  navigator.geolocation.getCurrentPosition((position) => {
    const center = [position.coords.longitude, position.coords.latitude];
    zoomToCenter(center);
  });
};

const onClickHome = () => {
  const { homeExtent } = window;
  if (homeExtent) {
    zoomToFeature(homeExtent);
  }
};

const setDefaultExtent = (shape: GeoJSON.Polygon | undefined) => {
  const { AUS_Extent, wkid } = Settings;
  const { loadedMapModules } = window;
  const { Extent, Polygon, SpatialReference } = loadedMapModules;
  let extent = null;
  if (shape) {
    const geometry = geojsonToArcGIS(shape);
    const polygon = new Polygon(geometry);
    extent = polygon.extent;
  } else {
    extent = new Extent({
      ...AUS_Extent,
      spatialReference: new SpatialReference({ wkid })
    });
  }
  window.homeExtent = extent;
};

const CurrentLocButton: React.FC = () => {
  return (
    <Button
      kind="tertiary"
      size="field"
      renderIcon={Location16}
      iconDescription="My current location"
      tooltipPosition="right"
      hasIconOnly
      className="map--current-loc-button bx--tooltip--hidden"
      onClick={onClickCurrentLocation}
    />
  );
};

const HomeButton: React.FC = () => {
  return (
    <Button
      kind="tertiary"
      size="field"
      renderIcon={Home16}
      iconDescription="Home"
      tooltipPosition="right"
      hasIconOnly
      className="map--home-button bx--tooltip--hidden"
      onClick={onClickHome}
    />
  );
};

const { modules, AUS_Extent, wkid, polygonSymbol, polygonDarkSymbol } =
  Settings;

const Map: React.FC<Props> = (props) => {
  const mapRef = useRef<HTMLDivElement | null>(null);
  const activeToolRef = useRef<string>('');
  const {
    map,
    mapView,
    extent,
    spatialReference,
    graphic,
    graphicsLayer,
    sketchViewModel,
    geometryEngine,
    polygon,
    identityManager,
    config
  } = modules;
  const [isLoaded, setIsLoaded] = useState(false);
  const [defaultBaseMap, setDefaultBaseMap] = useState<string>(
    MAP_DEFAULT_VAR.BASEMAP
  );
  const {
    showCurrentLocationButton,
    showHomeButton,
    extraModules,
    onSelect,
    onDrawing,
    onDeleteDrawing,
    onCompleteDrawing,
    onCancelDrawing,
    onEditDrawingStart,
    onReady,
    showSearchButton,
    showMapStyleButton,
    onOpenBasemap,
    maxZoomLevel,
    popUpEnabledLayers,
    onPointerOverUserRef
  } = props;
  const defaultModules = [
    map,
    mapView,
    extent,
    spatialReference,
    graphic,
    graphicsLayer,
    sketchViewModel,
    geometryEngine,
    polygon,
    identityManager,
    config
  ];
  const modulesPrepareToLoad = extraModules
    ? [...defaultModules, ...extraModules]
    : [...defaultModules];
  const moduleNamespace = modulesPrepareToLoad.map((m) => m.namespace);
  const moduleKey = modulesPrepareToLoad.map((m) => m.key);
  const teamBaseMapId = useSelector(selectCurrentTeamBaseMapId);
  const teamBaseMapType = useSelector(selectCurrentTeamBaseMapType);
  const teamBaseMapToken = useSelector(selectCurrentTeamBaseMapToken);
  const teamShape = useSelector(selectCurrentTeamShape);
  const baseMap = useSelector(selectBaseMap);
  const customBaseMap = useSelector(selectCustomBaseMap);
  const user = useSelector(currentUser);
  const privateTeamBaseMap = useSelector(selectPivateTeamBaseMap);
  const screenSize = useSelector(selectScreenSize);
  const dispatch = useDispatch();
  const baseMapTheme = baseMap?.theme;

  useEffect(() => {
    const { loadedSketchViewModel, loadedMap } = window;
    if (loadedSketchViewModel) {
      loadedSketchViewModel.polygonSymbol =
        baseMapTheme === 'dark' ? polygonDarkSymbol : polygonSymbol;
      loadedSketchViewModel.activeLineSymbol = {
        type: 'simple-line',
        style: 'short-dash',
        color: baseMapTheme === 'dark' ? '#ffffff' : '#002d9c',
        width: 1
      };
      loadedSketchViewModel.activeVertexSymbol = {
        type: 'simple-marker',
        style: 'circle',
        size: 3,
        color: baseMapTheme === 'dark' ? '#ffffff' : '#002d9c',
        outline: {
          style: 'none'
        }
      };
      loadedSketchViewModel.polylineSymbol = {
        type: 'simple-line',
        style: 'solid',
        color: baseMapTheme === 'dark' ? '#ffffff' : '#002d9c',
        width: 1
      };
      loadedSketchViewModel.vertexSymbol = {
        type: 'simple-marker',
        style: 'circle',
        size: 3,
        color: baseMapTheme === 'dark' ? '#ffffff' : '#002d9c',
        outline: {
          style: 'none'
        }
      };
    }
    if (loadedMap) {
      const drawingLayer = loadedMap.findLayerById('drawing-layer');
      if (drawingLayer) {
        drawingLayer.graphics.items.forEach((graphic: any) => {
          graphic.symbol =
            baseMapTheme === 'dark' ? polygonDarkSymbol : polygonSymbol;
        });
      }
    }
  }, [baseMapTheme]);

  useEffect(() => {
    user &&
      privateTeamBaseMap &&
      privateTeamBaseMap[user.id] &&
      setDefaultBaseMap(privateTeamBaseMap[user.id]);
  }, [user, privateTeamBaseMap]);

  useEffect(() => {
    if (isLoaded) {
      const { loadedMapModules } = window;
      const { IdentityManager } = loadedMapModules;
      const basemap =
        basemaps.find((b) => b.id === defaultBaseMap) || basemaps[0];
      let basemapId: string = basemap.id;
      let fromPortal: boolean = basemap.fromPortal;
      let theme: 'light' | 'dark' = basemap.theme;
      let isCustom: boolean = false;
      if (teamBaseMapType !== undefined) {
        if (teamBaseMapType !== 'custom') {
          basemapId = teamBaseMapType;
          fromPortal = false;
          if (teamBaseMapType === 'satellite') {
            theme = 'dark';
          }
        } else {
          if (teamBaseMapToken && teamBaseMapId) {
            if (typeof teamBaseMapToken === 'string') {
              dispatch(addAlert('error', 'Basemap Error', teamBaseMapToken));
            } else {
              IdentityManager.destroyCredentials();
              IdentityManager.registerToken({
                server: 'http://www.arcgis.com/sharing/rest',
                userId: 'dummy',
                token: teamBaseMapToken.access_token,
                ssl: false,
                expires:
                  new Date().getTime() + teamBaseMapToken.expires_in * 1000
              });
              basemapId = teamBaseMapId;
              fromPortal = true;
              isCustom = true;
            }
          } else {
            return;
          }
        }
      }
      dispatch(
        setBaseMap({
          id: basemapId,
          fromPortal,
          isCustom,
          theme
        })
      );
    }
  }, [
    isLoaded,
    teamBaseMapId,
    teamBaseMapType,
    teamBaseMapToken,
    dispatch,
    defaultBaseMap
  ]);

  useEffect(() => {
    if (isLoaded) {
      const { loadedMap, loadedView, loadedMapModules } = window;
      const { Config } = loadedMapModules;
      if (baseMap !== undefined) {
        if (baseMap.isCustom) {
          delete Config.apiKey;
          dispatch(loadCustomBaseMapStart({ id: baseMap.id }));
        } else {
          Config.apiKey = process.env.REACT_APP_ESRI_API_KEY;
          dispatch(loadCustomBaseMapSuccess(undefined));
          const basemap: BaseMap = baseMap.fromPortal
            ? {
                portalItem: {
                  id: baseMap.id
                }
              }
            : baseMap.id;
          loadedMap.basemap = basemap;
          loadedView.popup.close();
        }
      }
    }
  }, [isLoaded, baseMap, dispatch]);

  useEffect(() => {
    if (isLoaded) {
      const { loadedMap, loadedView } = window;
      if (customBaseMap !== undefined) {
        if (typeof customBaseMap === 'string') {
          dispatch(addAlert('error', 'Basemap Error', customBaseMap));
        } else {
          loadedMap.basemap = customBaseMap;
          loadedView.popup.close();
        }
      }
    }
  }, [isLoaded, customBaseMap, dispatch]);

  useEffect(() => {
    isLoaded && setDefaultExtent(teamShape);
  }, [teamShape, isLoaded]);

  useComponentMount(() => {
    loadModules(moduleNamespace, { css: true })
      .then((loadedModules: any[]) => {
        const mapModules: any = {};
        loadedModules.forEach((m, inx) => {
          mapModules[moduleKey[inx]] = m;
        });
        const {
          GraphicsLayer,
          Extent,
          SpatialReference,
          Map,
          MapView,
          SketchViewModel
        } = mapModules;
        const drawingLayer = new GraphicsLayer({ id: 'drawing-layer' });
        const extent = new Extent({
          ...AUS_Extent,
          spatialReference: new SpatialReference({ wkid })
        });
        const map = new Map({
          basemap: {
            portalItem: {
              id: MAP_DEFAULT_VAR.BASEMAP
            }
          },
          layers: [drawingLayer]
        });

        const view = new MapView({
          container: mapRef.current,
          map,
          extent: window.lastVisitedExtent || extent,
          zoom: window.lastVisitedZoom || -1,
          constraints: {
            maxZoom: maxZoomLevel || 22,
            minZoom: screenSize && screenSize !== SCREEN_SIZE.SM ? 3 : 1
          }
        });

        const sketchViewModel = new SketchViewModel({
          view,
          polygonSymbol:
            baseMapTheme === 'dark' ? polygonDarkSymbol : polygonSymbol,
          activeLineSymbol: {
            type: 'simple-line',
            style: 'short-dash',
            color: baseMapTheme === 'dark' ? '#f1c21b' : '#002d9c',
            width: 1
          },
          activeVertexSymbol: {
            type: 'simple-marker',
            style: 'circle',
            size: 3,
            color: baseMapTheme === 'dark' ? '#f1c21b' : '#002d9c',
            outline: {
              style: 'none'
            }
          },
          polylineSymbol: {
            type: 'simple-line',
            style: 'solid',
            color: baseMapTheme === 'dark' ? '#f1c21b' : '#002d9c',
            width: 1
          },
          vertexSymbol: {
            type: 'simple-marker',
            style: 'circle',
            size: 3,
            color: baseMapTheme === 'dark' ? '#f1c21b' : '#002d9c',
            outline: {
              style: 'none'
            }
          },
          layer: drawingLayer
        });

        if (showHomeButton) {
          const homeButton = document.createElement('div');
          homeButton.className = 'map--home-button-container';
          view.ui.add(homeButton, 'top-left');
        }

        if (showSearchButton) {
          const searchButton = document.createElement('div');
          searchButton.className = 'map--search-container';
          view.ui.add(searchButton, 'top-left');
        }

        if (showCurrentLocationButton) {
          const currentLocButton = document.createElement('div');
          currentLocButton.className = 'map--current-loc-button-container';
          view.ui.add(currentLocButton, 'top-left');
        }

        if (showMapStyleButton) {
          const mapStyleButton = document.createElement('div');
          mapStyleButton.className = 'map--style-container';
          view.ui.add(mapStyleButton, 'top-left');
        }

        view.on('click', (event: __esri.ViewClickEvent) => {
          view.graphics.removeAll();
          view.hitTest({ x: event.x, y: event.y }).then((repsonse: any) => {
            typeof onSelect === 'function' && onSelect(repsonse?.results);
            if (
              Array.isArray(repsonse?.results) &&
              repsonse.results.length > 0
            ) {
              repsonse.results.forEach((r: any) => {
                if (r?.graphic?.layer?.id === 'mis-pin-layer') {
                  removePin();
                }
              });
            }
          });
        });

        view.on('pointer-move', (event: __esri.ViewPointerMoveEvent) => {
          view.hitTest({ x: event.x, y: event.y }).then((repsonse: any) => {
            typeof onPointerOverUserRef === 'function' &&
              onPointerOverUserRef(repsonse?.results);
            if (
              Array.isArray(repsonse?.results) &&
              repsonse.results.length > 0
            ) {
              let hasPin = false;
              repsonse.results.forEach((r: any) => {
                if (r?.graphic?.layer?.id === 'mis-pin-layer') {
                  hasPin = true;
                  hoverPin(
                    true,
                    repsonse.screenPoint,
                    r?.graphic?.attributes?.name
                  );
                }
              });

              !hasPin && hoverPin(false, repsonse.screenPoint);
            }
          });
        });

        view.on('layerview-create', function (event: any) {
          if (
            event.layer.popupEnabled &&
            !popUpEnabledLayers?.includes(event.layer.id)
          ) {
            event.layer.popupEnabled = false;
          }
        });

        view.ui.padding = 0;

        isBrowser
          ? view.ui.move('zoom', 'bottom-right')
          : view.ui.remove('zoom');

        view.when(() => {
          if (isBrowser) {
            //add icon into zoom button after map view is ready
            const zoomInButton = view.ui
              .find('zoom')
              ?.domNode.getElementsByClassName('esri-icon-plus')
              ?.item(0);
            const zoomOutButton = view.ui
              .find('zoom')
              ?.domNode.getElementsByClassName('esri-icon-minus')
              ?.item(0);
            zoomInButton && ReactDom.render(<Add16 />, zoomInButton);
            zoomOutButton && ReactDom.render(<Subtract16 />, zoomOutButton);
          }

          if (showCurrentLocationButton) {
            const CLBContainer = document
              .getElementsByClassName('map--current-loc-button-container')
              .item(0);
            CLBContainer && ReactDom.render(<CurrentLocButton />, CLBContainer);
          }

          if (showHomeButton) {
            const HBContainer = document
              .getElementsByClassName('map--home-button-container')
              .item(0);
            HBContainer && ReactDom.render(<HomeButton />, HBContainer);
          }

          if (showMapStyleButton) {
            const MapStyleContainer = document
              .getElementsByClassName('map--style-container')
              .item(0);
            MapStyleContainer &&
              ReactDom.render(
                <Provider store={store}>
                  <MapStyleControllerButton onOpenBasemap={onOpenBasemap} />
                </Provider>,
                MapStyleContainer
              );
          }

          if (showSearchButton) {
            const SearchContainer = document
              .getElementsByClassName('map--search-container')
              .item(0);
            SearchContainer &&
              ReactDom.render(
                <Provider store={store}>
                  <MapLocationSearchButton buttonClassName="bx--tooltip--hidden" />
                </Provider>,
                SearchContainer
              );
          }

          if (window.viewEnquiryOnMap) {
            const { loadedMapModules } = window;
            const { Polygon } = loadedMapModules;
            const geometry = geojsonToArcGIS(window.viewEnquiryOnMap);
            const polygon = new Polygon(geometry);
            window.loadedView.goTo(polygon.extent);
            delete window.viewEnquiryOnMap;
          }
        });

        map.watch('basemap.loadError', (e: any) => {
          if (e?.message) {
            dispatch(addAlert('error', 'Basemap Error', e.message));
          }
        });

        const createEventHandler = (
          event: __esri.SketchViewModelCreateEvent
        ) => {
          switch (event.state) {
            case 'start':
              activeToolRef.current = '';
              break;
            case 'active':
              activeToolRef.current = event.tool;
              typeof onDrawing === 'function' &&
                onDrawing({
                  tool: event.tool,
                  graphic: event.graphic,
                  drawingLayer
                });
              break;
            case 'complete':
              if (activeToolRef.current === event.tool) {
                typeof onCompleteDrawing === 'function' &&
                  onCompleteDrawing({
                    tool: event.tool,
                    graphic: event.graphic,
                    drawingLayer
                  });
              } else {
                drawingLayer.remove(event.graphic);
                typeof onCancelDrawing === 'function' &&
                  onCancelDrawing({
                    drawingLayer
                  });
              }
              activeToolRef.current = '';
              break;
            case 'cancel':
              typeof onCancelDrawing === 'function' &&
                onCancelDrawing({
                  drawingLayer
                });
              break;
            default:
              break;
          }
        };

        const updateEventHandler = (
          event: __esri.SketchViewModelUpdateEvent
        ) => {
          if (
            event.state === 'active' &&
            event.graphics.length > 0 &&
            typeof onDrawing === 'function'
          ) {
            onDrawing({
              tool: event.tool,
              graphics: event.graphics,
              drawingLayer
            });
          }
          if (
            event.state === 'complete' &&
            event.graphics.length > 0 &&
            typeof onCompleteDrawing === 'function'
          ) {
            onCompleteDrawing({
              tool: event.tool,
              graphics: event.graphics,
              drawingLayer
            });
          }
          if (
            event.state === 'start' &&
            typeof onEditDrawingStart === 'function'
          ) {
            onEditDrawingStart({
              tool: event.tool,
              graphics: event.graphics,
              drawingLayer
            });
          }
        };

        const deleteEventHandler = (
          event: __esri.SketchViewModelDeleteEvent
        ) => {
          if (
            event.graphics.length > 0 &&
            typeof onDeleteDrawing === 'function'
          ) {
            onDeleteDrawing({
              tool: event.tool,
              graphics: event.graphics,
              drawingLayer
            });
          }
        };

        sketchViewModel.on('create', createEventHandler);
        sketchViewModel.on('update', updateEventHandler);
        sketchViewModel.on('delete', deleteEventHandler);

        if (!isLoaded) {
          window.loadedMap = map;
          window.loadedView = view;
          window.loadedSketchViewModel = sketchViewModel;
          window.loadedMapModules = mapModules;
          typeof onReady === 'function' && onReady();
        }
        setIsLoaded(true);
      })
      .catch(() => {});

    return () => {
      if (window.loadedView) {
        dispatch(loadCustomBaseMapSuccess(undefined));
        dispatch(setBaseMap(undefined));
        window.lastVisitedExtent = window.loadedView.extent;
        window.lastVisitedZoom = window.loadedView.zoom;
        delete window.loadedMap;
        delete window.loadedView;
        delete window.loadedSketchViewModel;
        delete window.loadedMapModules;
      }
    };
  });
  return (
    <div className={`map-container ${isLoaded ? '--loaded' : ''}`}>
      <div className="map" ref={mapRef} data-testid="map" />
      {!isLoaded && (
        <div className="map-loader">
          <Loading description="Active loading indicator" withOverlay={false} />
        </div>
      )}
    </div>
  );
};

const propTypes = {
  showCurrentLocationButton: PropTypes.bool,
  showHomeButton: PropTypes.bool,
  extraModules: PropTypes.array,
  onDrawing: PropTypes.func,
  onDeleteDrawing: PropTypes.func,
  onCompleteDrawing: PropTypes.func,
  onSelect: PropTypes.func,
  onCancelDrawing: PropTypes.func,
  onEditDrawingStart: PropTypes.func,
  onReady: PropTypes.func,
  showSearchButton: PropTypes.bool,
  showMapStyleButton: PropTypes.bool,
  onOpenBasemap: PropTypes.func,
  maxZoomLevel: PropTypes.number,
  popUpEnabledLayers: PropTypes.array,
  onPointerOverUserRef: PropTypes.func
};

type Props = PropTypes.InferProps<typeof propTypes>;

Map.propTypes = propTypes;

Map.defaultProps = {
  showCurrentLocationButton: false,
  showHomeButton: false,
  extraModules: [],
  popUpEnabledLayers: []
};

export default React.memo(Map);
